import AuthService from 'services/api/Auth';
import handlePromise from 'utils/Promise';
import { isSsoEnabled } from 'utils/authMode';
import packageData from '../../package.json';

const TAG = '[APP INFO]';

let appInfoLogged = false;

export type AppInfoPayload = {
  message?: string;
  version?: string;
  env?: string;
  runtime?: string;
  buildTime?: string | null;
  buildId?: string | null;
  authMode?: string;
  ssoEnabled?: boolean;
  apiHost?: string;
  updateApiHost?: string;
  apiVersion?: string;
  apiVersionStatus?: 'ok' | 'error';
};

const getUrlHost = ( url?: string ) => {
  if ( !url ) return undefined;
  try {
    return new URL( url ).host;
  } catch {
    return undefined;
  }
};

export const getClientAppInfo = (): AppInfoPayload => ( {
  message: 'ok',
  version: packageData.version,
  env: process.env.REACT_APP_APP_ENV || '—',
  runtime: process.env.NODE_ENV || '—',
  buildTime: process.env.REACT_APP_BUILD_TIME || null,
  buildId: process.env.REACT_APP_BUILD_ID || null,
  authMode: process.env.REACT_APP_AUTH_MODE || 'sso',
  ssoEnabled: isSsoEnabled(),
  apiHost: getUrlHost( process.env.REACT_APP_API_URL ),
  updateApiHost: getUrlHost( process.env.REACT_APP_UPDATE_API_URL ),
} );

export const fetchApiVersionInfo = async (): Promise<
Pick<AppInfoPayload, 'apiVersion' | 'apiVersionStatus'>
> => {
  const [, response, data] = await handlePromise( AuthService.getApiVersion() );
  if ( response.ok && data?.version ) {
    return {
      apiVersion: data.version,
      apiVersionStatus: 'ok',
    };
  }
  return { apiVersionStatus: 'error' };
};

export const logAppInfo = ( payload?: AppInfoPayload ) => {
  if ( typeof window === 'undefined' || appInfoLogged ) return;
  appInfoLogged = true;

  const info = payload ?? getClientAppInfo();
  const version = info.version ?? '—';
  const env = info.env ?? '—';
  const runtime = info.runtime ?? '—';

  console.groupCollapsed( `${TAG} Version ${version} · ${env} · ${runtime}` );
  console.info( 'message:', info.message ?? 'ok' );
  console.info( 'version:', info.version ?? '—' );
  console.info( 'env:', info.env ?? '—' );
  console.info( 'runtime:', info.runtime ?? '—' );
  if ( info.buildTime ) console.info( 'buildTime:', info.buildTime );
  if ( info.buildId ) console.info( 'buildId:', info.buildId );
  console.info( 'authMode:', info.authMode ?? '—' );
  console.info( 'ssoEnabled:', info.ssoEnabled ?? false );
  if ( info.apiHost ) console.info( 'apiHost:', info.apiHost );
  if ( info.updateApiHost ) console.info( 'updateApiHost:', info.updateApiHost );
  if ( info.apiVersion ) console.info( 'apiVersion:', info.apiVersion );
  if ( info.apiVersionStatus ) console.info( 'apiVersionStatus:', info.apiVersionStatus );
  console.groupEnd();
};

export const loadAndLogAppInfo = async () => {
  const clientInfo = getClientAppInfo();
  const apiInfo = await fetchApiVersionInfo();
  logAppInfo( { ...clientInfo, ...apiInfo } );
};
